'use client'
import { useStatsigClient } from '@statsig/react-bindings'
import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react'

import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { isNonNullish } from '@src/utils/functions/utils'

import { IExperimentLoggingData } from '../experiments.types'

import { statsigExperimentManager } from './statsig-experiment.manager'
import { StatsigInitContext } from './statsig.context'
import { StatsigExperimentBucket } from './statsig.types'

export function StatsigUserAwareProvider({
  children,
  serverSideExperiments,
}: PropsWithChildren<{ serverSideExperiments: StatsigExperimentBucket[] }>) {
  const client = useStatsigClient()
  const getExperimentBucket = useCallback(
    (experimentName: STATSIG_EXPERIMENTS | null) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!experimentName || !STATSIG_EXPERIMENTS_CONFIG[experimentName]) return null
      const fallback = STATSIG_EXPERIMENTS_CONFIG[experimentName].defaultBucket
      try {
        const experimentConfig = client.getExperiment(experimentName)
        if (experimentConfig.groupName) {
          statsigExperimentManager.setExposedBuckets(
            [
              {
                experimentName,
                bucketName: experimentConfig.groupName,
                isActive: experimentConfig.__evaluation?.is_experiment_active ?? false,
              },
            ],
            false,
          )
        }
        return experimentConfig.groupName ?? fallback
      } catch {
        return fallback
      }
    },
    [client],
  )
  useEffect(() => {
    if (client.isLoading) return
    statsigExperimentManager.initialize() // load from local storage
    const experimentNames = [...serverSideExperiments, ...statsigExperimentManager.getExposedBuckets()].map(
      u => u.experimentName,
    )

    const buckets = experimentNames.map((experimentName: string): IExperimentLoggingData | null => {
      const isKnownExperiment =
        experimentName &&
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        (STATSIG_EXPERIMENTS_CONFIG[experimentName as STATSIG_EXPERIMENTS] ||
          serverSideExperiments.some(exp => exp.experimentName === experimentName))

      if (!isKnownExperiment) {
        return null
      }
      const experimentConfig = client.getExperiment(experimentName, { disableExposureLog: true })
      if (!experimentConfig.groupName) {
        return null
      }

      return {
        experimentName,
        bucketName: experimentConfig.groupName,
        isActive: experimentConfig.__evaluation?.is_experiment_active ?? false,
      }
    })

    const convertedBuckets = buckets.filter(isNonNullish)
    statsigExperimentManager.setExposedBuckets(convertedBuckets, true)
  }, [client, serverSideExperiments])

  const value = useMemo(
    () => ({ init: client.client.loadingStatus === 'Ready', getExperimentBucket }),
    [client.client.loadingStatus, getExperimentBucket],
  )
  return <StatsigInitContext.Provider value={value}>{children}</StatsigInitContext.Provider>
}
